import React, { useEffect, useRef, useState } from "react";
import { SidePopup } from "../../../components/model-popup/side-popup";
import { CustomButton } from "../../../components/buttons/button";
import { toast } from "react-toastify";
import { WordTitle } from "../../../utils/commonFunction";

// formik
import { useFormik } from "formik";
import * as yup from "yup";

// components
import { RadioButtonField } from "../../../components/input-fields/RadioButtonField";
import Table from "../../../components/table/table";
import NotesDocs from "../notes_docs";

// api
import { getDetectedPatientListById } from "../../../api/list";
import { UpdateNotesSourceDetailsAPI } from "../../../api/update";

// --- Workaround so SidePopup accepts children
const SidePopupAny = SidePopup as React.ComponentType<any>;

// --- Multiple location popup
export const MultipleLocationPopup = ({
  showModal,
  setShowModal,
  title,
  handleClose,
  poupDetails,
  afterChangeNotesList
}: any) => {
  const [detectedPatientList, setDetectedPatientList] = useState<any[]>([]);
  const [detectedPatientListLoading, setDetectedPatientListLoading] = useState(false);
  const [buttonLoader, setButtonLoader] = useState<boolean>(false);
  const [tableHeight, setTableHeight] = useState(300);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const handleGetDetectedPatientList = async () => {
    setDetectedPatientListLoading(true);
    await getDetectedPatientListById(poupDetails?.id)
      .then((res: any) => {
        if (res?.success) {
          setDetectedPatientList(res?.result || []);
        } else {
          toast.error(res?.message || "Failed to get detected patient list");
        }
        setDetectedPatientListLoading(false);
      });
  };


  const formik = useFormik<{
    patient_id: number | null;
  }>({
    enableReinitialize: true,
    initialValues: {
      patient_id: poupDetails?.patient_id || null,
    },
    validationSchema: yup.object({
      patient_id: yup.number().required("Location is required"),
    }),
    onSubmit: async (values) => {
      setButtonLoader(true);
      await UpdateNotesSourceDetailsAPI(poupDetails?.id, values)
        .then(async (res: any) => {

          if (res?.result) {
            toast.success(res?.result?.message || "Notes details updated successfully");
            setShowModal(false);
            afterChangeNotesList();
          } else {
            if (res?.message)
              toast.error(res?.message || "Notes details updated failed");
          }
        })
        .catch((err: any) => {
          console.error(err);
        });

      setButtonLoader(false);
    },
  });

  useEffect(() => {
    if (showModal) {
      handleGetDetectedPatientList();
    }
  }, [showModal]);

  useEffect(() => {
    if (!detectedPatientListLoading && tableContainerRef.current) {
      setTableHeight(tableContainerRef.current.clientHeight || 300);
    }
  }, [detectedPatientListLoading, showModal]);

  return (
    <SidePopupAny
      showModal={showModal}
      handleClose={handleClose}
      width="max-w-[1000px]"
      title={title}
      footerStyle="flex justify-end gap-[16px] "
      footerContent={
        <>
          <CustomButton
            buttonStyle="text-[#031227] bg-white hover:bg-[#F5F5F5] disabled:bg-white disabled:text-[#AAADB1]  px-[26px] py-[12px] text-[14px] font-semibold leading-[17px] border-[#E8ECF4] "
            buttonName="Cancel"
            primary={false}
            loader={buttonLoader}
            onClick={() => {
              setShowModal(false)
            }}
          />
          <CustomButton
            buttonName="Update"
            buttonStyle="text-white px-[36px] py-[12px] text-[14px] font-semibold leading-[17px] hover:bg-[#166B5E] disabled:bg-[#75C8BB]  "
            primary
            loader={buttonLoader}
            onClick={async () => {
              const errors = await formik.validateForm();
              if (errors.patient_id) {
                toast.error(errors.patient_id);
                return;
              }
              formik.handleSubmit();
            }}
            type="button"
          />
        </>
      }
    >
      <div className="grid grid-cols-[49%_49%]  gap-x-2">

        <NotesDocs id={poupDetails?.id} />

        {/* Notes Details and Update Location */}
        <div className="border border-gray-200 rounded-lg shadow-sm h-[calc(100vh-180px)] flex flex-col overflow-hidden">
          {/* Source Note Details — fixed height */}
          <div className="font-normal text-gray-800 text-base bg-gray-200 py-2.5 px-4 rounded-t-lg flex-shrink-0">Source Note Details</div>
          <div className="grid grid-cols-[30%_70%] gap-y-1 p-3 rounded text-sm flex-shrink-0">
            <div className="font-medium">Patient Name:</div>
            <div className="max-w-[250px] truncate">
              {![null, "", undefined].includes(poupDetails?.patient_last_name) && ![null, "", undefined].includes(poupDetails?.patient_first_name)
                ? `${WordTitle(poupDetails?.patient_last_name || '')}, ${WordTitle(poupDetails?.patient_first_name || '')}`
                : (WordTitle(poupDetails?.patient_last_name || '') || WordTitle(poupDetails?.patient_first_name || '') || 'N/A')}
            </div>
            <hr className="my-[0.2rem] shadow-md col-span-2" />
            <div className="font-medium">Date of Birth:</div>
            <div className="max-w-[250px] truncate">
              {poupDetails?.DOB || '' || 'N/A'}
            </div>
            <hr className="my-[0.2rem] shadow-md col-span-2" />
            <div className="font-medium">Date of Service:</div>
            <div className="max-w-[250px] truncate" title={poupDetails?.encounter_date || ''}>
              {poupDetails?.encounter_date || '' || 'N/A'}
            </div>
            <hr className="my-[0.2rem] shadow-md col-span-2" />
            <div className="font-medium">Sender:</div>
            <div className="max-w-[250px] truncate" title={poupDetails?.sender_name || ''}>
              {WordTitle(poupDetails?.sender_name || '') || 'N/A'}
            </div>
            <hr className="my-[0.2rem] shadow-md col-span-2" />
            <div className="font-medium">Location:</div>
            <div className="max-w-[250px] truncate" title={poupDetails?.location || ''}>
              {WordTitle(poupDetails?.location || '') || 'N/A'}
            </div>
            <hr className="my-[0.2rem] shadow-md col-span-2" />
            <div className="font-medium">Error description:</div>
            <div className="max-w-[250px] truncate" title={poupDetails?.error_detail || ''}>
              {WordTitle(poupDetails?.error_detail || '') || ''}
            </div>
          </div>

          {/* Detected Patients — fills all remaining space */}
          <div className="font-normal text-gray-800 text-base bg-gray-200 py-2 px-4 flex-shrink-0">System Detected Patients</div>
          <div className="flex-1 overflow-hidden" ref={tableContainerRef}>
            <Table
              loading={detectedPatientListLoading}
              data={detectedPatientList}
              hasNextPage={false}
              tableHeight={tableHeight}
              columns={["", "Location", "Patient Name"]}
              scopedSlots={{
                "": ({ row }: any) => (
                  <td className="px-3 py-2 text-center">
                    <RadioButtonField
                      id={row?.id}
                      name="patient_id"
                      value={row?.id}
                      checked={row?.id == formik.values.patient_id}
                      onChange={formik.handleChange}
                      radioStyle="w-[20px] h-[20px]"
                      labelStyle="text-sm"
                    />
                  </td>
                ),
                Location: ({ row }: any) => (
                  <td className="px-3 py-2">{WordTitle(row?.location || '')}</td>
                ),
                "Patient Name": ({ row }: any) => (
                  <td className="px-3 py-2">
                    <div className="relative group inline-block cursor-pointer">
                      <span>
                        {![null, "", undefined].includes(row?.lastName) && ![null, "", undefined].includes(row?.firstName)
                          ? `${WordTitle(row?.lastName || '')}, ${WordTitle(row?.firstName || '')}`
                          : (WordTitle(row?.lastName || '') || WordTitle(row?.firstName || '') || 'N/A')}
                      </span>
                      <div className="absolute right-full top-1/2 -translate-y-1/2 mr-3 w-max bg-white border border-gray-300 text-gray-800 text-xs rounded px-3 py-2 shadow-lg hidden group-hover:block transition-opacity duration-300 z-10">
                        {row?.birthDate ? `DOB: ${row.birthDate}` : 'DOB: N/A'}
                        <br />
                        {row?.gender ? `Gender: ${WordTitle(row.gender)}` : 'Gender: N/A'}
                        <br />
                        {row?.middleName ? `Middle Name: ${row.middleName}` : 'Middle Name: N/A'}
                        <div className="absolute top-1/2 left-full -translate-y-1/2 -translate-x-1/2 w-2 h-2 bg-white border-t border-r border-gray-300 rotate-45 shadow-md z-10"></div>
                      </div>
                    </div>
                  </td>
                ),
              }}
            />
          </div>
        </div>
      </div>
    </SidePopupAny>
  );
};
